/**
 * Input Sanitization Utilities
 * Provides functions to sanitize and validate user inputs to prevent XSS attacks
 */

/**
 * Sanitize string input by removing potentially dangerous characters
 * @param {string} input - The input string to sanitize
 * @param {Object} options - Sanitization options
 * @returns {string} Sanitized string
 */
export const sanitizeString = (input, options = {}) => {
  if (typeof input !== "string") {
    return String(input || "");
  }

  const {
    allowNewlines = false,
    allowSpecialChars = true,
    maxLength = null,
    trim = true,
  } = options;

  let sanitized = input;

  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, "");

  // Remove script tags and event handlers
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, "");
  sanitized = sanitized.replace(/on\w+\s*=\s*[^\s>]*/gi, "");

  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, "");

  // Remove data: URLs that could be used for XSS (SVG/XML can carry script payloads)
  sanitized = sanitized.replace(/data:(?!image\/(?:png|jpg|jpeg|gif|webp)(?:[;,]))/gi, "");

  // Remove iframe tags
  sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, "");

  // Remove object and embed tags
  sanitized = sanitized.replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, "");
  sanitized = sanitized.replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, "");

  // Remove style tags that could contain malicious CSS
  sanitized = sanitized.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "");

  // Remove newlines if not allowed
  if (!allowNewlines) {
    sanitized = sanitized.replace(/[\r\n]/g, " ");
  }

  // Trim whitespace
  if (trim) {
    sanitized = sanitized.trim();
  }

  // Limit length if specified
  if (maxLength && sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
};

/**
 * Sanitize task name input
 * @param {string} taskName - Task name to sanitize
 * @returns {string} Sanitized task name
 */
export const sanitizeTaskName = (taskName) => {
  if (!taskName) return "";
  
  return sanitizeString(taskName, {
    allowNewlines: false,
    allowSpecialChars: true,
    maxLength: 500, // Reasonable limit for task names
    trim: true,
  });
};

/**
 * Sanitize project name input
 * @param {string} projectName - Project name to sanitize
 * @returns {string} Sanitized project name
 */
export const sanitizeProjectName = (projectName) => {
  if (!projectName) return "";
  
  return sanitizeString(projectName, {
    allowNewlines: false,
    allowSpecialChars: true,
    maxLength: 200,
    trim: true,
  });
};

/**
 * Sanitize tag name input
 * @param {string} tagName - Tag name to sanitize
 * @returns {string} Sanitized tag name
 */
export const sanitizeTagName = (tagName) => {
  if (!tagName) return "";
  
  return sanitizeString(tagName, {
    allowNewlines: false,
    allowSpecialChars: true,
    maxLength: 100,
    trim: true,
  });
};

/**
 * Sanitize client name input
 * @param {string} clientName - Client name to sanitize
 * @returns {string} Sanitized client name
 */
export const sanitizeClientName = (clientName) => {
  if (!clientName) return "";
  
  return sanitizeString(clientName, {
    allowNewlines: false,
    allowSpecialChars: true,
    maxLength: 200,
    trim: true,
  });
};

/**
 * Sanitize sprint name input
 * @param {string} sprintName - Sprint name to sanitize
 * @returns {string} Sanitized sprint name
 */
export const sanitizeSprintName = (sprintName) => {
  if (!sprintName) return "";
  
  return sanitizeString(sprintName, {
    allowNewlines: false,
    allowSpecialChars: true,
    maxLength: 200,
    trim: true,
  });
};

/**
 * Validate and sanitize ID (should be alphanumeric or UUID format)
 * @param {string} id - ID to validate and sanitize
 * @returns {string|null} Sanitized ID or null if invalid
 */
export const sanitizeId = (id) => {
  if (!id) return null;
  
  const idString = String(id).trim();
  
  // Allow alphanumeric, hyphens, and underscores (common in IDs)
  // Also allow MongoDB ObjectId format (24 hex characters)
  if (/^[a-zA-Z0-9_-]+$/.test(idString) || /^[a-fA-F0-9]{24}$/.test(idString)) {
    return idString;
  }
  
  // If it doesn't match expected patterns, sanitize aggressively
  return sanitizeString(idString, {
    allowNewlines: false,
    allowSpecialChars: false,
    maxLength: 100,
    trim: true,
  }).replace(/[^a-zA-Z0-9_-]/g, "");
};

/**
 * Validate boolean value
 * @param {any} value - Value to validate as boolean
 * @returns {boolean} Validated boolean
 */
export const sanitizeBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lower = value.toLowerCase().trim();
    return lower === "true" || lower === "1" || lower === "yes";
  }
  return Boolean(value);
};

/**
 * Sanitize time string (should be in valid time format)
 * @param {string} timeString - Time string to sanitize
 * @returns {string|null} Sanitized time string or null if invalid
 */
export const sanitizeTimeString = (timeString) => {
  if (!timeString) return null;
  
  const sanitized = sanitizeString(String(timeString), {
    allowNewlines: false,
    allowSpecialChars: false,
    maxLength: 50,
    trim: true,
  });
  
  // Validate time format (HH:MM:SS or HH:MM)
  if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/.test(sanitized)) {
    return sanitized;
  }
  
  return null;
};

/**
 * Escape HTML entities to prevent XSS
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
export const escapeHtml = (str) => {
  if (typeof str !== "string") {
    return String(str || "");
  }
  
  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  
  return str.replace(/[&<>"']/g, (m) => map[m]);
};

/**
 * Validate that a value is safe to store in localStorage
 * @param {any} value - Value to validate
 * @returns {boolean} True if safe to store
 */
export const isSafeForStorage = (value) => {
  // Check for functions (not safe)
  if (typeof value === "function") {
    return false;
  }
  
  // Check for undefined (not safe)
  if (value === undefined) {
    return false;
  }
  
  // Check size (localStorage has ~5-10MB limit)
  try {
    const serialized = JSON.stringify(value);
    if (serialized.length > 5000000) { // 5MB limit
      return false;
    }
  } catch (e) {
    return false;
  }
  
  return true;
};
